<script setup lang="ts">
import { useGlobalCounter } from '@/composables/useGlobalCounter';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import { computed, useAttrs, useTemplateRef, type Ref } from 'vue';

defineOptions({
    // disable automatic attr inheritance: we do not want to pass attr to the root element,
    // but to the inner field instead
    inheritAttrs: false,
});

export type KpTextFieldProps = {
    label?: string;
    inputId?: string;
    inputClass?: string | object;
    inputType?: string;
    error?: string | null;
};

const value: Ref<any> = defineModel();
const props = withDefaults(defineProps<KpTextFieldProps>(), {
    inputId: () => useGlobalCounter('field'),
    inputClass: '',
    inputType: 'text',
    error: '',
});

const cls = useAttrs().class || null;
// Remove attributes for the inner field we do NOT want to pass along:
const fieldAttrs = computed(() => {
    const returnObj = { ...useAttrs() };
    delete returnObj.id;
    delete returnObj.class;
    delete returnObj.label;
    delete returnObj['input-id'];
    delete returnObj['input-class'];
    delete returnObj['input-type'];
    return returnObj;
});

const emit = defineEmits<{
    (e: 'change', value: any): void;
    (e: 'enter'): void;
}>();

function onKeypress(e: KeyboardEvent) {
    if (e.key.toLowerCase() === 'enter') {
        emit('enter');
    }
}

function onChange(value: any) {
    emit('change', value);
}
</script>

<template>
    <div :class="`field ${cls}`">
        <label v-if="label" :for="inputId" v-html="label"></label>
        <InputText
            :id="inputId"
            v-model="value"
            :class="inputClass"
            :type="inputType"
            v-bind="fieldAttrs"
            @keypress="onKeypress"
            @value-change="onChange"
        />
        <Message v-if="error" class="error" size="small" severity="error" variant="simple">{{ error }}</Message>
    </div>
</template>

<style lang="css" scoped>
.error {
    --p-message-text-font-weight: normal;
}
</style>
